import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { companyInfo } from "../data/mock";

const navItems = [
  { label: "Home", href: "#home" },
  { label: "Diensten", href: "#diensten" },
  { label: "Zakelijk", to: "/zakelijk" },
  { label: "Prijzen", to: "/kosten-glazenwasser" },
  { label: "Over Ons", href: "#over-ons" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Anchors werken alleen op de homepage; op subpagina's linken we terug naar /#sectie.
  const renderItem = (item, className) => {
    const testId = `nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
    if (item.to) {
      return (
        <Link key={item.label} to={item.to} className={className} data-testid={testId} onClick={() => setIsMobileMenuOpen(false)}>
          {item.label}
        </Link>
      );
    }
    return (
      <a key={item.label} href={isHome ? item.href : `/${item.href}`} className={className} data-testid={testId} onClick={() => setIsMobileMenuOpen(false)}>
        {item.label}
      </a>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3" data-testid="header-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_sparkling-cleven/artifacts/unn65btx_logo-cleven-cleaning.png"
              alt="Cleven Cleaning Logo"
              className={`h-12 w-auto transition-all duration-300 ${isScrolled ? "" : "brightness-0 invert"}`}
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-5 xl:space-x-7" aria-label="Hoofdnavigatie">
            {navItems.map((item) =>
              renderItem(
                item,
                `font-medium transition-colors hover:text-sky-500 ${isScrolled ? "text-slate-700" : "text-white/90 hover:text-white"}`
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <a
              href={`tel:${companyInfo.phoneClean}`}
              className="flex items-center space-x-2 text-sky-600 hover:text-sky-700 font-semibold transition-colors"
              data-testid="header-phone-link"
            >
              <Phone className="w-5 h-5" />
              <span>{companyInfo.phone}</span>
            </a>
            <Button
              asChild
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40 transition-all"
            >
              <a href="#calculator" data-testid="header-calculator-btn">Bereken Prijs</a>
            </Button>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Sluit menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? "text-slate-800" : "text-white"}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? "text-slate-800" : "text-white"}`} />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white rounded-2xl shadow-xl mt-2 p-4 absolute left-4 right-4" role="dialog" aria-label="Mobiel navigatiemenu" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-4" aria-label="Mobiele navigatie">
              {navItems.map((item) => renderItem(item, "text-slate-700 font-medium hover:text-sky-500 transition-colors"))}
              <a href={`tel:${companyInfo.phoneClean}`} className="flex items-center space-x-2 text-sky-600 font-semibold">
                <Phone className="w-5 h-5" />
                <span>{companyInfo.phone}</span>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
